import { useState } from 'react';
import {
  HUSTLE_REPORT_REASONS,
  formatPence,
  type HustleAction,
  type HustleContent,
  type HustleJobView,
  type HustleReportReason,
  type HustleStats,
  type HustleStatus,
} from '@london-life/contracts';
import { londonWhen, typedPence } from '../lib/format';

const STATUS: Record<HustleStatus, string> = {
  open: 'On the Board',
  offered: 'Offer sent',
  hired: 'Under way',
  done: 'Marked done',
  paid: 'Paid ✅',
  refunded: 'Called off · money back',
  cancelled: 'Taken down',
  expired: 'Ran out of time',
  removed: 'Taken down after reports',
};

// a worker's record: stars from reviews, and jobs done (as Lagos Life shows it)
export function StatsLine({ stats }: { stats: HustleStats }) {
  return (
    <small className="hustle-stats">
      {stats.jobs
        ? `${stats.stars !== null ? `★ ${stats.stars.toFixed(1)}` : 'No stars yet'}${stats.reviews ? ` (${stats.reviews})` : ''} · ${stats.jobs} ${stats.jobs === 1 ? 'job' : 'jobs'} done`
        : 'New to Hustle'}
    </small>
  );
}

// One job: what it is, its fee, who, where it stands, and the buttons for whoever is looking (the poster, the
// worker or someone on the Board). Every button asks the server.
export function JobCard({
  job,
  hustle,
  busy,
  onAct,
  onLook,
}: {
  job: HustleJobView;
  hustle: HustleContent;
  busy: boolean;
  onAct: (body: HustleAction) => void;
  // a decorate job: open the copy of the home (the decorator) or the look (the poster)
  onLook?: () => void;
}) {
  const [asking, setAsking] = useState<'report' | 'call_off' | null>(null);
  const [budget, setBudget] = useState(job.decor?.budgetPence ? String(job.decor.budgetPence / 100) : '');
  // a decorate job with its decorator's work (jobs hired before the decorator existed have none)
  const decor = job.kind === 'decorate' ? job.decor : null;
  const kind = hustle.kinds.find((item) => item.kind === job.kind);
  const other = job.mine ? job.worker : job.poster;
  const button = (label: string, body: HustleAction, main = false) => (
    <button
      type="button"
      className={main ? 'city-card-go' : 'flights-terminal'}
      disabled={busy}
      onClick={() => onAct(body)}
    >
      {label}
    </button>
  );
  return (
    <li className={`hustle-job ${job.status}`}>
      <span aria-hidden="true">{kind?.emoji ?? '🧰'}</span>
      <span>
        <strong>
          {kind?.label ?? 'A job'} · {formatPence(job.feePence)}
        </strong>
        <p>{job.details}</p>
        {other && (
          <small>
            {job.mine ? 'Worker' : 'Posted by'} {other.name} · <StatsLine stats={other.stats} />
          </small>
        )}
        <em>
          {STATUS[job.status]}
          {job.direct && job.status === 'offered' && job.working ? ' to you' : ''}
          {job.expiresAt ? ` · until ${londonWhen(job.expiresAt)}` : ''}
          {job.payAt ? ` · pays itself ${londonWhen(job.payAt)}` : ''}
          {job.sentBack && job.status === 'hired' ? ' · sent back once' : ''}
          {job.status === 'open' && job.applicantCount ? ` · ${job.applicantCount} applied` : ''}
        </em>
        {decor && <DecorLine job={job} />}

        {/* the poster: who applied, and the next step */}
        {job.mine && job.status === 'open' && job.applicants.length > 0 && (
          <ul className="hustle-applicants" aria-label={`Who applied for ${kind?.label ?? 'the job'}`}>
            {job.applicants.map((person) => (
              <li key={person.lifeId}>
                <span>
                  <strong>{person.name}</strong>
                  <StatsLine stats={person.stats} />
                </span>
                {button(`Hire · ${formatPence(job.feePence)}`, { action: 'hire', lifeId: person.lifeId }, true)}
              </li>
            ))}
          </ul>
        )}
        <span className="cars-actions">
          {job.mine && job.status === 'open' && (
            <>
              {job.bumpFrom ? (
                <small className="hustle-later">⬆️ Bump again {londonWhen(job.bumpFrom)}</small>
              ) : (
                button('⬆️ Bump', { action: 'bump' })
              )}
              {button('Take it down', { action: 'cancel' })}
            </>
          )}
          {job.mine && job.status === 'offered' && button('Take the offer back', { action: 'cancel' })}
          {job.mine && job.status === 'done' && (
            <>
              {button(`Pay ${formatPence(job.feePence)}`, { action: 'pay' }, true)}
              {!job.sentBack && !decor && button('Not done yet', { action: 'send_back' })}
            </>
          )}
          {/* the poster of a decorate job: review the look, the keys, the budget */}
          {job.mine &&
            decor &&
            decor.state === 'sent' &&
            (job.status === 'done' || job.status === 'paid') &&
            onLook && (
              <button type="button" className="city-card-go" onClick={onLook}>
                👀 Review the look
              </button>
            )}
          {job.mine &&
            decor &&
            job.status === 'hired' &&
            button(decor.keys ? 'Take the keys back' : 'Give the keys back', { action: 'keys', on: !decor.keys })}
          {job.mine && decor?.waitUntil && job.status === 'hired' && (
            <small className="hustle-later">Waiting for their changes</small>
          )}
          {job.mine &&
            job.status === 'hired' &&
            !decor?.waitUntil &&
            (asking === 'call_off' ? (
              <span className="jet-sell">
                <span>Call it off? The money comes back to you.</span>
                <button type="button" onClick={() => setAsking(null)} disabled={busy}>
                  Keep it
                </button>
                <button
                  type="button"
                  className="jet-sell-yes"
                  disabled={busy}
                  onClick={() => onAct({ action: 'call_off' })}
                >
                  Call it off
                </button>
              </span>
            ) : (
              <button type="button" className="flights-terminal" onClick={() => setAsking('call_off')}>
                Call it off…
              </button>
            ))}
          {job.mine && job.status === 'paid' && job.stars === null && (
            <span className="hustle-stars" role="group" aria-label="Review the job">
              {[1, 2, 3, 4, 5].map((stars) => (
                <button
                  key={stars}
                  type="button"
                  disabled={busy}
                  aria-label={`${stars} ${stars === 1 ? 'star' : 'stars'}`}
                  onClick={() => onAct({ action: 'review', stars })}
                >
                  ★
                </button>
              ))}
            </span>
          )}
          {job.mine && job.stars !== null && (
            <small className="hustle-later">{'★'.repeat(job.stars)} your review</small>
          )}

          {/* the worker */}
          {job.working && job.status === 'offered' && (
            <>
              {button('Take the job', { action: 'accept' }, true)}
              {button('No thanks', { action: 'decline' })}
            </>
          )}
          {job.working && job.status === 'hired' && (
            <>
              {decor
                ? onLook && (
                    <button type="button" className="city-card-go" onClick={onLook}>
                      🛋️ Decorate {job.poster.name}'s home
                    </button>
                  )
                : button('Mark done', { action: 'done' }, true)}
              {button('Give it up', { action: 'give_up' })}
            </>
          )}
          {job.working && decor && job.status !== 'hired' && onLook && (
            <button type="button" className="flights-terminal" onClick={onLook}>
              👀 See the look
            </button>
          )}

          {/* someone on the Board */}
          {!job.mine && !job.working && job.status === 'open' && (
            <>
              {job.applied
                ? button('Take my name off', { action: 'withdraw' })
                : button('Apply', { action: 'apply' }, true)}
              {asking !== 'report' && (
                <button type="button" className="flights-terminal" onClick={() => setAsking('report')}>
                  Report
                </button>
              )}
            </>
          )}
        </span>
        {job.mine && decor && (job.status === 'hired' || job.status === 'done') && (
          <span className="hustle-budget">
            <label className="railco-field">
              <span>BUDGET FOR NEW PIECES (£)</span>
              <input
                inputMode="decimal"
                value={budget}
                placeholder="0"
                onChange={(event) => setBudget(event.target.value)}
              />
              <small>
                {decor.budgetPence
                  ? `${job.worker?.name ?? 'They'} may pick new pieces up to ${formatPence(decor.budgetPence)}, at shop prices. You pay only for a look you accept.`
                  : 'None: their things only. Nothing is held: you pay only for a look you accept.'}
              </small>
            </label>
            {button('Set budget', { action: 'budget', budgetPence: typedPence(budget) }, false)}
          </span>
        )}
        {asking === 'report' && (
          <div className="business-chips" role="group" aria-label="What is wrong with this post">
            {(Object.keys(HUSTLE_REPORT_REASONS) as HustleReportReason[]).map((reason) => (
              <button
                key={reason}
                type="button"
                disabled={busy}
                onClick={() => {
                  onAct({ action: 'report', reason });
                  setAsking(null);
                }}
              >
                {HUSTLE_REPORT_REASONS[reason]}
              </button>
            ))}
          </div>
        )}
      </span>
    </li>
  );
}

// where a decorator's work stands, for the poster and the decorator
function DecorLine({ job }: { job: HustleJobView }) {
  const decor = job.decor!;
  const other = job.mine ? (job.worker?.name ?? 'They') : job.poster.name;
  const line =
    decor.state === 'accepted'
      ? '✨ The look is in'
      : decor.state === 'sent'
        ? job.mine
          ? `✨ ${other}'s look is waiting for you`
          : `📨 Look sent: waiting for ${other}`
        : decor.state === 'changes'
          ? job.mine
            ? `✏️ You asked for changes: “${decor.note ?? ''}”`
            : `✏️ ${other} asked for changes: “${decor.note ?? ''}”`
          : decor.state === 'turned_down'
            ? job.mine
              ? `You passed on the last look: ${other} is trying another`
              : `${other} passed on your last look: try another`
            : !decor.keys
              ? job.mine
                ? `🔒 Keys taken back. ${other}'s draft is kept.`
                : `🔒 ${other} took the keys back for now`
              : job.mine
                ? `🔓 ${other} can work on a copy of your home until ${londonWhen(decor.accessUntil)}. Nothing changes until you accept a look.`
                : `🔓 From anywhere until ${londonWhen(decor.accessUntil)}, even when ${other} is offline`;
  return <small className="hustle-decor">{line}</small>;
}
